/**
 * Shared badge-style tag input.
 *
 * Renders the current `value` as pill chips, an inline text field for
 * adding new entries, and (optionally) a portal-anchored autocomplete
 * dropdown sourced from `searchTags(query)`.
 *
 * Designed for reuse across pages with different tag namespaces:
 *   - profiles → `searchProfileTags`
 *   - models   → `searchModelTags`
 *   - …or none → omit `searchTags` for a chip editor with no autocomplete
 *
 * Keyboard:
 *   - Enter / `,`  → commit current input (or highlighted suggestion)
 *   - Backspace    → on empty input, remove the last chip
 *   - ↓/↑          → navigate suggestions
 *   - Escape       → close suggestions
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { popoverPlacement } from './popover/usePopoverPosition';
import './TagInput.css';

/** The suggestions list's tallest (`.tag-input-suggestions` max-height). */
const SUGGESTIONS_MAX_H = 220;

export interface TagSuggestion {
  tag: string;
  count: number;
}

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional async suggestion source. Receives the current input text. */
  searchTags?: (query: string) => Promise<TagSuggestion[]>;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
  /** Maximum suggestions to render (default 12). */
  maxSuggestions?: number;
  /** Debounce window in ms for autocomplete fetches (default 200). */
  searchDebounceMs?: number;
}

export default function TagInput({
  value,
  onChange,
  searchTags,
  disabled = false,
  placeholder,
  id,
  className,
  maxSuggestions = 12,
  searchDebounceMs = 200,
}: Readonly<Props>) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [anchorRect, setAnchorRect] = useState<CSSProperties | null>(null);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Reposition the suggestions dropdown against the input wrap. */
  function refreshAnchor() {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flips above near the bottom edge, and stays inside the CAPCO banners (#2271).
    const place = popoverPlacement(r, { matchTriggerWidth: true, minWidth: 0, estimatedHeight: SUGGESTIONS_MAX_H });
    setAnchorRect({ ...place, zIndex: undefined, maxHeight: Math.min(SUGGESTIONS_MAX_H, Number(place.maxHeight)) });
  }

  // Track scroll/resize while the dropdown is open so the portal stays
  // pinned to the input even inside an `overflow-y: auto` modal.
  useLayoutEffect(() => {
    if (!open) return;
    refreshAnchor();
    const handler = () => refreshAnchor();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, suggestions.length, value.length]);

  // Click-outside dismissal.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // A blank input has no suggestions. Whenever what the search below depends on changes while
  // the input is blank, they are cleared here, during render, so no frame shows the previous
  // text's suggestions under a blank input (#2361). Same deps, same moments, as the search.
  const searchDeps = [text, value, open, disabled, searchTags, maxSuggestions, searchDebounceMs];
  const [searchedDeps, setSearchedDeps] = useState(searchDeps);
  if (searchDeps.some((dep, i) => !Object.is(dep, searchedDeps[i]))) {
    setSearchedDeps(searchDeps);
    if (!disabled && open && searchTags && text.trim() === '') {
      setSuggestions([]);
      setActive(0);
    }
  }

  // Debounced autocomplete fetch.
  useEffect(() => {
    if (disabled || !open || !searchTags) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim() === '') return;
    debounceRef.current = setTimeout(() => {
      searchTags(text)
        .then((results) => {
          const filtered = results
            .filter((s) => !value.includes(s.tag))
            .slice(0, maxSuggestions);
          setSuggestions(filtered);
          setActive(0);
        })
        .catch(() => setSuggestions([]));
    }, searchDebounceMs);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [text, value, open, disabled, searchTags, maxSuggestions, searchDebounceMs]);

  function commit(raw: string) {
    const t = raw.trim();
    if (!t) return;
    if (value.includes(t)) { setText(''); return; }
    onChange([...value, t]);
    setText('');
    setSuggestions([]);
    setActive(0);
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (open && suggestions[active]) commit(suggestions[active].tag);
      else commit(text);
    } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
      e.preventDefault();
      remove(value.at(-1)!);
    } else if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      // Showing suggestions: this Escape closes them and nothing else; the
      // prevented default tells a dialog around the input to stay (#2356).
      // Nothing showing: the Escape goes on and closes the dialog as before.
      if (open && suggestions.length > 0) e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div
      ref={wrapRef}
      className={`tag-input-wrap ${disabled ? 'is-disabled' : ''} ${className ?? ''}`}
      role="none"
      onClick={() => { if (!disabled) inputRef.current?.focus(); }}
    >
      {value.map((tag) => (
        <span key={tag} className="tag-input-chip">
          {tag}
          {!disabled && (
            <button
              type="button"
              className="tag-input-chip-remove"
              onClick={(e) => { e.stopPropagation(); remove(tag); }}
              aria-label={`Remove tag ${tag}`}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          ref={inputRef}
          id={id}
          className="tag-input-input"
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onBlur={(e) => {
            if (!wrapRef.current?.contains(e.relatedTarget)) {
              commit(text);
            }
          }}
          placeholder={value.length === 0 ? (placeholder ?? 'Add a tag…') : ''}
        />
      )}
      {open && suggestions.length > 0 && anchorRect && createPortal(
        <div
          className="tag-input-suggestions"
          style={anchorRect}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.tag}
              type="button"
              className={`tag-input-suggestion ${i === active ? 'is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); commit(s.tag); }}
              onMouseEnter={() => setActive(i)}
            >
              <span>{s.tag}</span>
              {s.count > 0 && <span className="tag-input-suggestion-count">{s.count}</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
